import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ligneVoie as composerLigneVoie } from '../../../utils/adresse';
// Reutilise les styles de carte titulaire (prestataire-card* + lmm-*) : meme rendu que la carte
// affichee dans la modale de lancement BC (aucune duplication de CSS).
import '../../../composants/LancerMarcheModal/LancerMarcheModal.css';
import { useProfile } from '../../../contexts/ProfileContext';
import ModalProfilMembre from '../../Publications/components/DetailCandidature/ModalProfilMembre';
import type { ProfileData } from '../../../services/profileService';

// Telephone au format FR (identique a TitulairesLotCards).
const formatPhone = (phone?: string | null): string => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  let national = digits;
  if (digits.startsWith('33') && digits.length === 11) national = digits.slice(2);
  else if (digits.startsWith('0') && digits.length === 10) national = digits.slice(1);
  else return phone;
  return `+33 ${national[0]} ${national.slice(1, 3)} ${national.slice(3, 5)} ${national.slice(5, 7)} ${national.slice(7, 9)}`;
};

// Carte "coordonnees" du prestataire connecte : sa propre carte de profil, telle qu'elle apparaitra a
// l'acheteur avec un chiffrage. Alimentee par le PROFIL (useProfile) et NON par l'annuaire d'un lot :
// elle s'affiche donc sans dependre du choix d'un lot technique / geographique. Purement decorative
// (aucun clic), meme rendu visuel que la carte titulaire de la modale BC.
const ProfilCardPrestataire: React.FC = () => {
  // Le ProfileProvider charge deja le profil pour les roles prestataire (effet sur isAuthenticated) :
  // rien a declencher ici, on se contente de lire.
  const { profile } = useProfile();
  const navigate = useNavigate();
  // Clic sur la carte -> modale profil en lecture seule, STRICTEMENT comme l'annuaire cote acheteur.
  // Point cle : ModalProfilMembre attend le payload de /annuaire/entites/{id}/profil. Le profil
  // d'edition expose par useProfile n'a pas la meme forme -> il rendait la modale incomplete. On
  // refait donc le meme fetch que TitulairesLotCards ; useProfile ne sert qu'a resoudre l'id d'entite.
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [annuaireProfile, setAnnuaireProfile] = useState<ProfileData | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  const entite = profile?.entite ?? null;
  const entreprise = profile?.entreprise ?? null;
  const coord = profile?.coordonnees ?? null;
  const adresse = entreprise?.adresse ?? null;

  const nom = (entreprise?.nom ?? entite?.nom ?? '').toUpperCase() || '-';
  const siret = entite?.siret ?? entreprise?.siret ?? null;
  const logoUrl = entite?.logo_url ?? null;

  const ligneVoie = composerLigneVoie(adresse?.numero_voie, adresse?.type_voie, adresse?.nom_voie);
  const ligneVille = [adresse?.code_postal, adresse?.ville]
    .filter((p) => p != null && String(p).trim().length > 0)
    .join(' ')
    .trim();
  const contactNom = [coord?.legal_prenom, coord?.legal_nom]
    .filter((p) => p != null && String(p).trim().length > 0)
    .join(' ')
    .trim();
  const email = (coord?.legal_email ?? '').trim();
  const tel = (coord?.legal_tel ?? '').trim();
  const fonction = (coord?.legal_fonction ?? '').trim();

  // Contact affiche dans la modale profil (meme forme que l'annuaire).
  const contactInfo = coord
    ? {
        nom: coord.legal_nom ?? '',
        prenom: coord.legal_prenom ?? '',
        email: coord.legal_email ?? '',
        telephone: coord.legal_tel ?? '',
        fonction: coord.legal_fonction ?? '',
      }
    : null;

  // Meme sequence que TitulairesLotCards.openProfil : on ouvre puis on charge, pour que la modale
  // affiche son etat de chargement plutot qu'un contenu vide.
  const openProfil = useCallback(() => {
    const entiteId = entite?.id;
    if (entiteId === undefined || entiteId === null) return;
    setAnnuaireProfile(null);
    setProfileError(null);
    setProfileLoading(true);
    setShowProfileModal(true);
    fetch(`${process.env.REACT_APP_API_URL}/annuaire/entites/${entiteId}/profil`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Erreur ${r.status}`);
        return r.json();
      })
      .then((d: { data?: ProfileData } & Partial<ProfileData>) =>
        setAnnuaireProfile((d?.data ?? d) as ProfileData),
      )
      .catch((e: Error) => setProfileError(e.message || 'Impossible de charger le profil.'))
      .finally(() => setProfileLoading(false));
  }, [entite?.id]);

  return (
    <>
      {/* Carte centree : TitulairesLotCards obtient le meme effet via son wrapper .lmm-titulaire-row ;
          rendue seule ici, elle a besoin de son propre conteneur centreur. */}
      <div className="profil-card-prestataire-row" style={{ display: 'flex', justifyContent: 'center' }}>
        <div
          className="prestataire-card prestataire-card--readonly lmm-titulaire-card"
          // pointerEvents auto : override le `.lmm-titulaire-card { pointer-events: none }` herite des
          // cartes display-only de la modale Lancer.
          style={{ width: '210px', cursor: 'pointer', pointerEvents: 'auto' }}
          role="button"
          tabIndex={0}
          title="Voir mon profil"
          onClick={openProfil}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openProfil();
            }
          }}
        >
        <div className="prestataire-card-inner">
        <div className="prestataire-card-header">
          <div className="prestataire-card-banner-placeholder" style={{ backgroundColor: '#f97316' }} />
        </div>
        <div className="prestataire-card-logo-wrapper">
          {logoUrl ? (
            <img src={logoUrl} alt={nom} className="prestataire-card-logo-img" />
          ) : (
            <span className="prestataire-card-logo-text">LOGO</span>
          )}
        </div>
        <div className="prestataire-card-body">
          <p className="prestataire-card-nom">{nom}</p>
          {siret && (
            <p className="lmm-card-info">
              <span className="lmm-card-info-label">SIRET</span>
              {siret}
            </p>
          )}
          {ligneVoie && <p className="lmm-card-info">{ligneVoie}</p>}
          {ligneVille && <p className="lmm-card-info">{ligneVille}</p>}
          {contactNom && (
            <p className="lmm-card-info lmm-card-info--contact">
              {contactNom}
              {fonction ? ` – ${fonction}` : ''}
            </p>
          )}
          {email && (
            <p className="lmm-card-info">
              <a href={`mailto:${email}`}>{email}</a>
            </p>
          )}
          {tel && <p className="lmm-card-info">{formatPhone(tel)}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Portal vers document.body : cette carte est rendue DANS la modale de creation, dont le CSS
          porte des selecteurs descendants (`.lancer-marche-modal h2 { text-align: center }`,
          `... label`, `... input/select { width: 100% }`). Imbriquee, la modale profil en heritait et
          ne rendait pas comme depuis l'annuaire. Le portal la sort du sous-arbre -> rendu identique.

          Le wrapper porte un contexte d'empilement explicite, volontairement au-dessus du maximum du
          projet (99999). Raison : `.modal-overlay` est declare dans ~15 CSS avec la MEME specificite,
          dont `pages/Assistance/Assistance.css` a `z-index: 9999` -> c'est l'ordre de chargement du
          bundle qui tranche, pas la valeur, et ce 9999 s'applique donc a TOUTES les modales, y compris
          celle de creation qui nous contient. Meme collision cote `.modal-backdrop` (quatre fois 1000)
          contre `.modal-profil-backdrop` (1100). On ne depend donc d'aucune de ces cascades.
          `position: relative` ne cree PAS de bloc conteneur pour les enfants `fixed` (contrairement a
          transform), le backdrop couvre donc toujours le viewport. */}
      {createPortal(
        <div style={{ position: 'relative', zIndex: 100000 }}>
          <ModalProfilMembre
            isOpen={showProfileModal}
            onClose={() => {
              setShowProfileModal(false);
              setAnnuaireProfile(null);
              setProfileError(null);
            }}
            profile={annuaireProfile}
            isLoading={profileLoading}
            error={profileError}
            contactInfo={contactInfo}
            initialTab={0}
            showCompletion={false}
            // Opt-in : ce bouton n'existe que dans CETTE modale (le presta regarde son propre profil),
            // jamais dans l'annuaire cote acheteur. Les onglets de la modale et ceux de /profil
            // portent les memes noms, et ProfilUnifie resout `?tab=` par nom (tabs.indexOf) -> on
            // renvoie sur l'onglet exact que le presta consultait, sans index magique.
            showModifier
            onModifier={(tabName) => navigate(`/profil?tab=${encodeURIComponent(tabName)}`)}
          />
        </div>,
        document.body,
      )}
    </>
  );
};

export default ProfilCardPrestataire;
